import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Download as DownloadIcon,
  Assignment as FormsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

// TKT-248 (Naomi): Super Admin surface for employee form submissions.
// Lets a Super Admin reset a previously-submitted form (e.g. updated Bank
// Details): the signed copy is archived for audit history and the employee
// sees the form as pending again, ready to re-fill. The whole HR Management
// section is superadmin-only (TKT-221), so this page inherits that gate.
const EmployeeFormsAdmin = () => {
  const { t, i18n } = useTranslation();
  const isSpanish = (i18n.language || 'en').toLowerCase().startsWith('es');

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formsData, setFormsData] = useState(null);
  const [formsLoading, setFormsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await makeApiCall('/api/users?limit=500&is_active=true&sort_by=first_name&sort_order=ASC');
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load users');
        setUsers(data.users || []);
      } catch (err) {
        console.error('Error loading users:', err);
        setError(err.message || t('employeeFormsAdmin.errors.loadUsers'));
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [t]);

  const fetchUserForms = useCallback(async (userId) => {
    setFormsLoading(true);
    setError('');
    try {
      const response = await makeApiCall(`/api/employee-forms/admin/user-forms/${userId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load forms');
      setFormsData(data);
    } catch (err) {
      console.error('Error loading user forms:', err);
      setFormsData(null);
      setError(err.message || t('employeeFormsAdmin.errors.loadForms'));
    } finally {
      setFormsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserForms(selectedUser.id);
    } else {
      setFormsData(null);
    }
  }, [selectedUser, fetchUserForms]);

  const userLabel = (u) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return name ? `${name} (${u.username})` : u.username;
  };

  const formName = (row) => (isSpanish ? row.form_name_es : row.form_name_en) || row.form_name_en;

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString(i18n.language || undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleDownload = async (submissionId, title) => {
    setError('');
    try {
      const response = await makeApiCall(`/api/employee-forms/${submissionId}/pdf`);
      if (!response.ok) throw new Error(t('employeeFormsAdmin.errors.download'));
      const blob = await response.blob();
      const safeTitle = String(title || 'signed-form').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'signed-form';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading form PDF:', err);
      setError(err.message || t('employeeFormsAdmin.errors.download'));
    }
  };

  const handleConfirmReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    setError('');
    setSuccess('');
    try {
      const response = await makeApiCall(`/api/employee-forms/${resetTarget.submission_id}/reset`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('employeeFormsAdmin.errors.reset'));
      setSuccess(t('employeeFormsAdmin.resetSuccess', { form: formName(resetTarget) }));
      setResetTarget(null);
      if (selectedUser) fetchUserForms(selectedUser.id);
    } catch (err) {
      console.error('Error resetting form:', err);
      setError(err.message || t('employeeFormsAdmin.errors.reset'));
    } finally {
      setResetting(false);
    }
  };

  const completedCount = formsData?.forms?.filter((f) => f.status === 'completed').length || 0;
  const pendingCount = formsData?.forms?.filter((f) => f.status === 'pending').length || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <FormsIcon sx={{ fontSize: 32, color: '#1565c0' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {t('employeeFormsAdmin.title')}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: '#666', mb: 3, maxWidth: 800 }}>
        {t('employeeFormsAdmin.description')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Autocomplete
          options={users}
          loading={usersLoading}
          value={selectedUser}
          onChange={(e, value) => setSelectedUser(value)}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('employeeFormsAdmin.selectEmployee')}
              aria-label={t('employeeFormsAdmin.selectEmployee')}
            />
          )}
          sx={{ maxWidth: 480 }}
        />
      </Paper>

      {formsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      )}

      {!formsLoading && formsData && (
        <>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip color="success" label={`${t('employeeFormsAdmin.completed')}: ${completedCount}`} />
            <Chip color="warning" label={`${t('employeeFormsAdmin.pending')}: ${pendingCount}`} />
            {formsData.archived?.length > 0 && (
              <Chip variant="outlined" label={`${t('employeeFormsAdmin.archivedCount')}: ${formsData.archived.length}`} />
            )}
          </Stack>

          <Paper sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small" aria-label={t('employeeFormsAdmin.formsTableLabel')}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>{t('employeeFormsAdmin.form')}</strong></TableCell>
                    <TableCell><strong>{t('employeeFormsAdmin.status')}</strong></TableCell>
                    <TableCell><strong>{t('employeeFormsAdmin.submitted')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('employeeFormsAdmin.actions')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formsData.forms.map((form) => (
                    <TableRow key={form.form_type_id}>
                      <TableCell>{formName(form)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={form.status === 'completed' ? 'success' : 'default'}
                          label={form.status === 'completed'
                            ? t('employeeFormsAdmin.completed')
                            : t('employeeFormsAdmin.pending')}
                        />
                      </TableCell>
                      <TableCell>{formatDate(form.submitted_at)}</TableCell>
                      <TableCell align="right">
                        {form.status === 'completed' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownload(form.submission_id, formName(form))}
                              aria-label={`${t('employeeFormsAdmin.download')} ${formName(form)}`}
                            >
                              {t('employeeFormsAdmin.download')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ResetIcon />}
                              onClick={() => setResetTarget(form)}
                              aria-label={`${t('employeeFormsAdmin.reset')} ${formName(form)}`}
                            >
                              {t('employeeFormsAdmin.reset')}
                            </Button>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {formsData.archived?.length > 0 && (
            <Paper>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('employeeFormsAdmin.archivedTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {t('employeeFormsAdmin.archivedDescription')}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small" aria-label={t('employeeFormsAdmin.archivedTableLabel')}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>{t('employeeFormsAdmin.form')}</strong></TableCell>
                      <TableCell><strong>{t('employeeFormsAdmin.submitted')}</strong></TableCell>
                      <TableCell><strong>{t('employeeFormsAdmin.resetAt')}</strong></TableCell>
                      <TableCell><strong>{t('employeeFormsAdmin.resetBy')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('employeeFormsAdmin.actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formsData.archived.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{formName(row)}</TableCell>
                        <TableCell>{formatDate(row.submitted_at)}</TableCell>
                        <TableCell>{formatDate(row.archived_at)}</TableCell>
                        <TableCell>
                          {`${row.archived_by_first_name || ''} ${row.archived_by_last_name || ''}`.trim() || row.archived_by_username || '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(row.id, formName(row))}
                            aria-label={`${t('employeeFormsAdmin.download')} ${formName(row)}`}
                          >
                            {t('employeeFormsAdmin.download')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <Dialog
        open={Boolean(resetTarget)}
        onClose={() => !resetting && setResetTarget(null)}
        aria-labelledby="reset-form-dialog-title"
      >
        <DialogTitle id="reset-form-dialog-title">
          {t('employeeFormsAdmin.resetDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('employeeFormsAdmin.resetDialogBody', {
              form: resetTarget ? formName(resetTarget) : '',
              employee: selectedUser ? userLabel(selectedUser) : ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)} disabled={resetting} aria-label={t('common.cancel')}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmReset}
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={18} color="inherit" /> : <ResetIcon />}
            aria-label={t('employeeFormsAdmin.resetConfirm')}
          >
            {t('employeeFormsAdmin.resetConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeFormsAdmin;
